import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Divider, FormControlLabel, Grid, Stack, TextField, Typography,
} from '@mui/material';
import { Baby, Syringe, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';
import { ImmunizationDto, ObstetricProfileDto } from '../../lib/api/modules/specialty';

interface SpecialtyCarePanelProps {
  patientId: string;
  /** 'GYNECOLOGY_OB' or 'PEDIATRICS' — the doctor's resolved department template key. */
  templateKey: string;
}

/**
 * Specialty-specific patient management beyond the encounter form: obstetric profile tracking
 * (GYNECOLOGY_OB) or immunization schedule + growth percentile (PEDIATRICS). Only rendered when
 * the current doctor's department resolves to one of these template keys.
 */
export function SpecialtyCarePanel({ patientId, templateKey }: SpecialtyCarePanelProps) {
  if (templateKey === 'GYNECOLOGY_OB') return <ObstetricCare patientId={patientId} />;
  if (templateKey === 'PEDIATRICS') return <PediatricCare patientId={patientId} />;
  return null;
}

function ObstetricCare({ patientId }: { patientId: string }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ObstetricProfileDto>({});

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.obstetric.getProfile(patientId);
    if (res.data) setProfile(res.data);
    setLoading(false);
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    const res = await api.obstetric.saveProfile(patientId, profile);
    if (res.error) {
      toast.error(res.error);
    } else {
      toast.success('Obstetric profile saved');
      if (res.data) setProfile(res.data);
    }
    setSaving(false);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Baby size={20} />
          <Typography variant="h6" fontWeight={700}>Obstetric Profile</Typography>
          {profile.gestationalAgeWeeks != null && (
            <Chip label={`${profile.gestationalAgeWeeks} weeks gestation`} color="primary" size="small" sx={{ ml: 1 }} />
          )}
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth size="small" label="Gravida" type="number"
              value={profile.gravida ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, gravida: e.target.value ? Number(e.target.value) : undefined }))}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth size="small" label="Para" type="number"
              value={profile.para ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, para: e.target.value ? Number(e.target.value) : undefined }))}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" label="Last Menstrual Period" type="date"
              InputLabelProps={{ shrink: true }}
              value={profile.lastMenstrualPeriod ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, lastMenstrualPeriod: e.target.value || undefined }))}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" label="Expected Delivery Date" type="date"
              InputLabelProps={{ shrink: true }}
              value={profile.expectedDeliveryDate ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, expectedDeliveryDate: e.target.value || undefined }))}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!profile.highRisk}
                  onChange={(e) => setProfile((p) => ({ ...p, highRisk: e.target.checked }))}
                />
              }
              label="High-risk pregnancy"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" multiline rows={2} label="Notes"
              value={profile.notes ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, notes: e.target.value }))}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save Profile'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

function PediatricCare({ patientId }: { patientId: string }) {
  const [loading, setLoading] = useState(true);
  const [immunizations, setImmunizations] = useState<ImmunizationDto[]>([]);
  const [initializing, setInitializing] = useState(false);
  const [administeringId, setAdministeringId] = useState<string | null>(null);
  const [weightKg, setWeightKg] = useState('');
  const [percentileResult, setPercentileResult] = useState<{ percentile: number; zScore: number; disclaimer: string } | null>(null);
  const [percentileLoading, setPercentileLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.pediatric.listImmunizations(patientId);
    setImmunizations(res.data || []);
    setLoading(false);
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const handleInitialize = async () => {
    setInitializing(true);
    const res = await api.pediatric.initializeImmunizationSchedule(patientId);
    if (res.error) {
      toast.error(res.error);
    } else {
      toast.success('Immunization schedule initialized');
      setImmunizations(res.data || []);
    }
    setInitializing(false);
  };

  const handleAdminister = async (immunizationId: string) => {
    setAdministeringId(immunizationId);
    const res = await api.pediatric.administerDose(patientId, immunizationId, {
      administeredDate: new Date().toISOString().split('T')[0],
    });
    if (res.error) {
      toast.error(res.error);
    } else {
      toast.success('Dose recorded');
      await load();
    }
    setAdministeringId(null);
  };

  const handleGrowthCheck = async () => {
    if (!weightKg) return;
    setPercentileLoading(true);
    const res = await api.pediatric.growthPercentile(patientId, Number(weightKg));
    if (res.error) {
      toast.error(res.error);
      setPercentileResult(null);
    } else if (res.data) {
      setPercentileResult(res.data);
    }
    setPercentileLoading(false);
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Syringe size={20} />
            <Typography variant="h6" fontWeight={700}>Immunization Schedule</Typography>
          </Stack>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
          ) : immunizations.length === 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No immunization schedule recorded yet for this patient.
              </Typography>
              <Button variant="outlined" onClick={handleInitialize} disabled={initializing}>
                {initializing ? <CircularProgress size={18} /> : 'Initialize Standard Schedule'}
              </Button>
            </Box>
          ) : (
            <Stack spacing={1}>
              {immunizations.map((dose) => (
                <Box
                  key={dose.id}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {dose.vaccineName} — Dose {dose.doseNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dose.status === 'ADMINISTERED'
                        ? `Given ${dose.administeredDate}`
                        : `Due ${dose.dueDate || 'N/A'}`}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={dose.status}
                      size="small"
                      color={dose.status === 'ADMINISTERED' ? 'success' : dose.status === 'MISSED' ? 'error' : 'warning'}
                    />
                    {dose.status === 'DUE' && (
                      <Button
                        size="small" variant="outlined"
                        onClick={() => handleAdminister(dose.id)}
                        disabled={administeringId === dose.id}
                      >
                        {administeringId === dose.id ? <CircularProgress size={14} /> : 'Mark Given'}
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <TrendingUp size={20} />
            <Typography variant="h6" fontWeight={700}>Growth Percentile (Weight-for-Age)</Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              size="small" label="Weight (kg)" type="number"
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value)}
            />
            <Button variant="contained" onClick={handleGrowthCheck} disabled={!weightKg || percentileLoading}>
              {percentileLoading ? <CircularProgress size={18} /> : 'Calculate'}
            </Button>
          </Stack>
          {percentileResult && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1">
                Estimated percentile: <strong>{percentileResult.percentile.toFixed(0)}th</strong>
                {' '}(z-score {percentileResult.zScore.toFixed(2)})
              </Typography>
              <Alert severity="info" sx={{ mt: 1 }}>{percentileResult.disclaimer}</Alert>
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
